import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import cn from "classnames";
// Config
import { isDevelopment } from "../../../config/env";
// Store
import { useStore } from "../../../store";
// Api
import { createFieldRoutesCustomer } from "../../../api/mainApi";
// Components
import Button from "../../../components/Button/Button";
import Radio from "../../../components/Radio/Radio";
import DayPicker from "../../../components/DayPicker/DayPicker";
import TimePicker from "../../../components/TimePicker/TimePicker";
import ContactUsToast from "../../../components/Toast/ContactUsToast/ContactUsToast";
import FullScreenLoader from "../../../components/FullScreenLoader/FullScreenLoader";
// Styles
import styles from "./ScheduleContent.module.scss";
// Image
import scheduleImage from "../../../assets/images/schedule_image.png";
import calendarIcon from "../../../assets/images/calendar_icon.png";
import timeIcon from "../../../assets/images/time_icon.png";
import { extractAddressComponents } from "../../../helpers/address";
// JSON
import postalCodesData from "../../../assets/json/postal_codes.json";
//Amplitude
import * as amplitude from "@amplitude/analytics-browser";

export const SCHEDULE_TYPES = {
  FIRST_AVAILABLE: "first_available",
  SPECIFIC_DAY_TIME: "specific_day_time",
};

const generalErrorEvent = {
  event_type: "Error",
  display_name: "Error",
  event_properties: {
    page_name: "eComm - Schedule 1st visit",
    error_name: "Something went wrong",
  },
};

const ScheduleContent = () => {
  const navigate = useNavigate();
  const {
    scheduleData,
    setScheduleData,
    contactInfo,
    addressObject,
    setFieldRoutesCustomer,
  } = useStore();

  const [scheduleType, setScheduleType] = useState(
    scheduleData.scheduleType ?? SCHEDULE_TYPES.FIRST_AVAILABLE
  );
  const [selectedDay, setSelectedDay] = useState(scheduleData.chosenDay ?? "");
  const [selectedTime, setSelectedTime] = useState(
    scheduleData.chosenTime ?? ""
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect to contact page if no contact info or address object
  useEffect(() => {
    if (!contactInfo || !addressObject) {
      navigate("/contact");
    }
  }, [addressObject, contactInfo, navigate]);

  const handleNextClick = async () => {
    const buttonClickedEvent = {
      event_type: "Button Clicked",
      display_name: "Button Clicked",
      event_properties: {
        page_name: "eComm - Schedule 1st visit",
        button_name: "Next",
      },
    };

    amplitude.track(buttonClickedEvent);

    try {
      setIsSubmitting(true);
      const formattedAddress = extractAddressComponents(addressObject);
      const officeId = postalCodesData.find(
        (item) => `${item["Zip"]}` === formattedAddress.postal_code
      )?.["Office ID"];

      if (!isDevelopment && !officeId) {
        amplitude.track(generalErrorEvent);

        toast(<ContactUsToast />, { theme: "dark" });
        return;
      }

      const addressWithUnitNumber =
        contactInfo.unitNumber === ""
          ? formattedAddress.line1
          : `${formattedAddress.line1} ${contactInfo.unitNumber}`;

      const { data } = await createFieldRoutesCustomer({
        fname: contactInfo.firstName,
        lname: contactInfo.lastName,
        address: addressWithUnitNumber,
        city: formattedAddress.city,
        state: formattedAddress.state,
        zip: formattedAddress.postal_code,
        lat: `${addressObject.geometry.location.lat}`,
        lng: `${addressObject.geometry.location.lng}`,
        phone1: contactInfo.mobilePhone,
        email: contactInfo.email,
        officeID: isDevelopment ? 19 : officeId,
      });

      if (data) {
        setScheduleData({
          scheduleType,
          chosenDay: selectedDay,
          chosenTime: selectedTime,
        });
        setFieldRoutesCustomer(data);

        const formSubmittedEvent = {
          event_type: "Form Submitted",
          display_name: "Form Submitted",
          event_properties: {
            page_name: "eComm - Schedule 1st visit",
            has_preference: scheduleType === "first_available" ? false : true,
            preferred_day: selectedDay,
            preferred_time: selectedTime,
          },
        };

        amplitude.track(formSubmittedEvent);

        setIsSubmitting(false);
        navigate("/checkout");
      } else {
        const errorEvent = {
          event_type: "Error",
          display_name: "Error",
          event_properties: {
            page_name: "eComm - Schedule 1st visit",
            error_name: "Failed to create Customer in FieldRoutes",
            error_source: "FieldRoutes",
          },
        };

        amplitude.track(errorEvent);

        setIsSubmitting(false);
        toast(<ContactUsToast />, { theme: "dark" });
      }
    } catch (error) {
      const errorEvent = {
        event_type: "Error",
        display_name: "Error",
        event_properties: {
          page_name: "eComm - Schedule 1st visit",
          error_name: "Failed to create Customer in FieldRoutes",
          error_source: "FieldRoutes",
        },
      };

      amplitude.track(errorEvent);

      setIsSubmitting(false);
      toast(<ContactUsToast />, { theme: "dark" });
    }
  };

  const handleBackClick = () => {
    const buttonClickedEvent = {
      event_type: "Button Clicked",
      display_name: "Button Clicked",
      event_properties: {
        page_name: "eComm - Schedule 1st visit",
        button_name: "Back",
      },
    };

    amplitude.track(buttonClickedEvent);

    navigate("/contact");
  };

  const handleOnDayChange = (day) => {
    if (day === selectedDay) {
      setSelectedDay("");
      return;
    }

    setSelectedDay(day);

    if (day === "Saturday") {
      setSelectedTime("8-12PM");
    }
  };

  const handleOnTimeChange = (time) => {
    if (time === selectedTime) {
      setSelectedTime("");
      return;
    }

    setSelectedTime(time);
  };

  useEffect(() => {
    const pageViewedEvent = {
      event_type: "Page Viewed",
      display_name: "Page Viewed",
      event_properties: {
        page_name: "eComm - Schedule 1st visit",
      },
    };

    amplitude.track(pageViewedEvent);
  }, []);

  return (
    <div className={styles.root}>
      {isSubmitting && <FullScreenLoader />}

      <div className={styles.pickerSection}>
        <h1 className={styles.title}>Schedule your initial visit</h1>
        <p className={styles.description}>
          Appointments are Monday-Friday 8AM-5:30PM and Saturdays from 8AM-12PM
        </p>

        <div
          className={cn(styles.preferenceSection, {
            [styles.showDayTime]:
              scheduleType === SCHEDULE_TYPES.SPECIFIC_DAY_TIME,
          })}
        >
          <h3 className={styles.sectionTitle}>Preference</h3>
          <Radio
            name="schedule_type"
            value={SCHEDULE_TYPES.FIRST_AVAILABLE}
            checked={scheduleType === SCHEDULE_TYPES.FIRST_AVAILABLE}
            label="Schedule the first available appointment within the next 2 business days"
            onChange={(e) => setScheduleType(e.target.value)}
          />

          <Radio
            name="schedule_type"
            value={SCHEDULE_TYPES.SPECIFIC_DAY_TIME}
            checked={scheduleType === SCHEDULE_TYPES.SPECIFIC_DAY_TIME}
            label="I need a certain day or time of the week"
            onChange={(e) => setScheduleType(e.target.value)}
          />
        </div>

        {scheduleType === SCHEDULE_TYPES.SPECIFIC_DAY_TIME && (
          <div className={styles.dayTimeSection}>
            <h3 className={styles.sectionTitle}>
              <img src={calendarIcon} alt="Preferred Day" /> Preferred Day
            </h3>
            <DayPicker
              selectedDay={selectedDay}
              onDayChange={handleOnDayChange}
            />

            <h3 className={styles.sectionTitle}>
              <img src={timeIcon} alt="Preferred Time " />
              {selectedDay === "Saturday" ? "Time:" : "Preferred Time"}
              {selectedDay === "Saturday" && (
                <span className={styles.saturdayText}>
                  &nbsp;Morning 8-12PM
                </span>
              )}
            </h3>
            <TimePicker
              selectedDay={selectedDay}
              selectedTime={selectedTime}
              onSelectTime={handleOnTimeChange}
            />
          </div>
        )}

        <div className={styles.buttonsContainer}>
          <Button
            block
            onClick={handleNextClick}
            disabled={
              scheduleType === SCHEDULE_TYPES.SPECIFIC_DAY_TIME &&
              !selectedDay &&
              !selectedTime
            }
          >
            Next
          </Button>
          <Button block type="outline" onClick={handleBackClick}>
            Back
          </Button>
        </div>

        <div className={styles.footerInfoSection}>
          <p>
            We’ll schedule your appointment and confirm it via email and text
            within 1 business day
          </p>

          <p className={styles.phoneParagraph}>
            Questions? Call or text{" "}
            <a href="tel:(855) 979-7378">(855) 979-7378</a>
          </p>
        </div>
      </div>
      <div className={styles.imageSection}>
        <img src={scheduleImage} alt="placeholder" />
      </div>
    </div>
  );
};

export default ScheduleContent;
